import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CssBaseline,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Link,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { Canvas } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import axios from 'axios';
import './App.css';
import CheckIcon from '@mui/icons-material/Check';
import StripeCheckout from './components/StripeCheckout';
import HomePage from './components/HomePage';
import FeedPage from './components/FeedPage';
import BathroomScene from './components/BathroomScene';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const WS_URL = API_URL.replace('http', 'ws') + '/ws/feed';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#86a2fc',
    },
    secondary: {
      main: '#8a989d',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    // fontFamily: "'Architects Daughter', sans-serif",
    fontFamily: "'Special Elite', sans-serif",
  },
});

// Updated RandomizedText component with useMemo
export const RandomizedText = ({ children, component = 'span', variant, ...props }) => {
  // Use useMemo to ensure stability between renders
  const randomizedWords = React.useMemo(() => {
    // First split the text by spaces to get words
    const words = String(children).split(/(\s+)/);

    return words.map((word, wordIndex) => {
      // If this is just whitespace, return it directly
      if (word.trim() === '') {
        return word;
      }

      // For actual words, randomize each character
      const characters = word.split('');
      const randomizedChars = characters.map((char, charIndex) => {
        // Generate random variations for each character
        const rotation = Math.random() * 10 - 5; // -5 to 5 degrees
        const yOffset = Math.random() * 2 - 1; // -1 to 1 px
        const xOffset = Math.random() * 1 - 0.5; // -0.5 to 0.5 px
        const scale = 0.8 + Math.random() * 0.4; // 0.8 to 1.2

        return (
          <span
            key={`${wordIndex}-${charIndex}`}
            style={{
              display: 'inline-block',
              transform: `rotate(${rotation}deg) translateY(${yOffset}px) translateX(${xOffset}px) scale(${scale})`,
              transformOrigin: 'center center',
            }}
          >
            {char}
          </span>
        );
      });

      // Wrap the randomized characters in a span to keep the word together
      return (
        <span key={`word-${wordIndex}`} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {randomizedChars}
        </span>
      );
    });
  }, [children]); // Only recalculate when children changes

  return (
    <Typography component={component} variant={variant} {...props}>
      {randomizedWords}
    </Typography>
  );
};

// Replace the AnimatedCamera component with a simpler one that just sets up the camera
// but doesn't animate it, since BathroomScene now controls the animation
function AnimatedCamera() {
  const cameraRef = useRef();

  // We no longer animate the camera here since BathroomScene handles the animation
  // Just set up the initial camera position and rotation
  return (
    <PerspectiveCamera
      ref={cameraRef}
      makeDefault
      position={[0, 1, 0]}
      rotation={[-0.5, 0, 0]}
      fov={70}
    />
  );
}

// Combined component that handles both routes
function MainContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [processedFilename, setProcessedFilename] = useState(null);
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [stickerMode, setStickerMode] = useState(false);
  const [lightMode, setLightMode] = useState(false);
  const [stickerPosition, setStickerPosition] = useState(null);
  const [userUploadedStickerImage, setUserUploadedStickerImage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isPlacingSticker, setIsPlacingSticker] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [stickerUploadError, setStickerUploadError] = useState(null);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [showEmbeddedCheckout, setShowEmbeddedCheckout] = useState(false);
  const fileInputRef = useRef(null);

  // Pagination states
  const [pageSize] = useState(20); // Number of items per page
  const [hasMore, setHasMore] = useState(true); // Whether there are more items to load
  const [page, setPage] = useState(1); // Current page number

  // Replace localStorage/sessionStorage state with React state
  const [newItemId, setNewItemId] = useState(null);
  const [pendingStripeSession, setPendingStripeSession] = useState(null);
  const [pendingStickers, setPendingStickers] = useState([]);

  // Track items that were specifically added via WebSocket in the current session
  // These should animate, while items loaded from localStorage should not
  const [animateItemId, setAnimateItemId] = useState(null);

  const socketRef = useRef(null);
  const feedItemsRef = useRef(feedItems);
  const newItemTimeoutRef = useRef(null);

  // Add a new state variable for tracking WebSocket expectation
  const [pollingTimeoutId, setPollingTimeoutId] = useState(null);

  // Update feedItemsRef when feedItems changes
  useEffect(() => {
    feedItemsRef.current = feedItems;
  }, [feedItems]);

  // Clear newItemId after 5 minutes
  useEffect(() => {
    if (newItemId) {
      const timeoutId = setTimeout(() => {
        setNewItemId(null);
      }, 300000); // 5 minutes

      return () => clearTimeout(timeoutId);
    }
  }, [newItemId]);

  // Clear animation state after a short delay
  useEffect(() => {
    if (animateItemId) {
      const timeoutId = setTimeout(() => {
        setAnimateItemId(null);
      }, 2000); // Animation should only be visible for 2 seconds

      return () => clearTimeout(timeoutId);
    }
  }, [animateItemId]);

  const handleProcessed = useCallback((filename) => {
    setProcessedFilename(filename);
  }, []);

  const handleReset = useCallback(() => {
    setProcessedFilename(null);
  }, []);

  const fetchFeed = useCallback(
    async (pageToLoad = 1, shouldReplace = false) => {
      try {
        setLoading(true);

        // Calculate offset based on page number and page size
        const offset = (pageToLoad - 1) * pageSize;

        const response = await axios.get(`${API_URL}/feed/?limit=${pageSize}&offset=${offset}`);
        const newItems = response.data.items || [];

        // If this is a fresh load (page 1), replace the existing items
        // Otherwise, append the new items to the existing ones
        if (shouldReplace || pageToLoad === 1) {
          setFeedItems(newItems);
        } else {
          setFeedItems((prevItems) => [...prevItems, ...newItems]);
        }

        // Check if there are more items to load
        setHasMore(newItems.length === pageSize);
        setError(null);

        // Update the current page
        setPage(pageToLoad);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching feed:', err);
        setError('Failed to load the feed. Please try again later.');
        if (pageToLoad === 1) {
          setFeedItems([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  // Initial load
  useEffect(() => {
    fetchFeed(1, true);
  }, [fetchFeed]);

  // Function to load more items
  const loadMoreItems = useCallback(() => {
    if (!loading && hasMore) {
      fetchFeed(page + 1);
    }
  }, [fetchFeed, loading, hasMore, page]);

  // WebSocket connection effect
  useEffect(() => {
    // Flag to prevent multiple connections
    let isActive = true;
    let socket = null;
    let pingTimeoutId = null;

    const wsConnectionId = Math.random().toString(36).substring(2, 8);
    // eslint-disable-next-line no-console
    console.log(
      `[WebSocket ${wsConnectionId}] Setting up WebSocket connection (attempt: ${connectionAttempts})`
    );

    const connectWebSocket = () => {
      try {
        // Only attempt connection if component is still mounted
        if (!isActive) {
          // eslint-disable-next-line no-console
          console.log(`[WebSocket ${wsConnectionId}] Aborting connection - component not active`);
          return;
        }

        // Close existing connection if there is one
        if (socketRef.current) {
          // eslint-disable-next-line no-console
          console.log(
            `[WebSocket ${wsConnectionId}] Closing existing connection before creating new one`
          );
          socketRef.current.close();
        }

        // Create new connection
        // eslint-disable-next-line no-console
        console.log(`[WebSocket ${wsConnectionId}] Attempting connection to ${WS_URL}`);
        socket = new WebSocket(WS_URL);
        socketRef.current = socket;

        // Set timeout to detect connection failures
        const connectionTimeoutId = setTimeout(() => {
          if (socket.readyState !== WebSocket.OPEN) {
            // eslint-disable-next-line no-console
            console.log(
              `[WebSocket ${wsConnectionId}] Connection timeout - socket not open after 5 seconds`
            );
            socket.close();
            // Will trigger the onclose handler which handles reconnection
          }
        }, 5000);

        socket.onopen = () => {
          clearTimeout(connectionTimeoutId);

          if (!isActive) {
            // Component unmounted during connection, close immediately
            // eslint-disable-next-line no-console
            console.log(
              `[WebSocket ${wsConnectionId}] Connection opened but component unmounted, closing socket`
            );
            socket.close();
            return;
          }
          // eslint-disable-next-line no-console
          console.log(`[WebSocket ${wsConnectionId}] Connected successfully`);

          // Set up ping timeout (if we don't get a ping from server within 90 seconds, connection is stale)
          if (isActive && socket && socket.readyState === WebSocket.OPEN) {
            pingTimeoutId = setTimeout(() => {
              // eslint-disable-next-line no-console
              console.log(
                `[WebSocket ${wsConnectionId}] No ping received in 90 seconds, closing connection`
              );
              socket.close();
              setConnectionAttempts(0);
            }, 90000);
          }
        };

        socket.onclose = (event) => {
          clearTimeout(connectionTimeoutId);
          if (pingTimeoutId) clearTimeout(pingTimeoutId);

          // eslint-disable-next-line no-console
          console.log(`[WebSocket ${wsConnectionId}] Disconnected`, event);

          // Try to reconnect with backoff unless component is unmounting
          if (isActive && connectionAttempts < 5) {
            const timeout = Math.min(1000 * 2 ** connectionAttempts, 30000);
            // eslint-disable-next-line no-console
            console.log(
              `[WebSocket ${wsConnectionId}] Will attempt reconnection in ${timeout}ms (attempt ${connectionAttempts + 1})`
            );
            setTimeout(() => {
              if (isActive) {
                setConnectionAttempts((prev) => prev + 1);
              }
            }, timeout);
          } else if (connectionAttempts >= 5) {
            // eslint-disable-next-line no-console
            console.log(
              `[WebSocket ${wsConnectionId}] Maximum reconnection attempts reached (${connectionAttempts})`
            );
          }
        };

        socket.onerror = (error) => {
          // eslint-disable-next-line no-console
          console.error(`[WebSocket ${wsConnectionId}] Error:`, error);
        };

        socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);

            if (message.type === 'ping') {
              // eslint-disable-next-line no-console
              console.log(`[WebSocket ${wsConnectionId}] Ping received, sending pong`);
              // Reset the ping timeout since we got a ping
              if (pingTimeoutId) clearTimeout(pingTimeoutId);

              // Set a new ping timeout
              if (isActive && socket && socket.readyState === WebSocket.OPEN) {
                pingTimeoutId = setTimeout(() => {
                  // eslint-disable-next-line no-console
                  console.log(
                    `[WebSocket ${wsConnectionId}] No ping received in 90 seconds, closing connection`
                  );
                  socket.close();
                }, 90000);
              }

              // Respond with pong to keep connection alive
              if (socket && socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: 'pong' }));
              }
              return;
            }

            if (message.type === 'new_audio') {
              // eslint-disable-next-line no-console
              console.log(
                `[WebSocket ${wsConnectionId}] Received new audio:`,
                message.data.filename
              );
              const newAudio = message.data;

              // Use ref to get the current feed items
              const currentFeedItems = feedItemsRef.current;

              // Check if the item already exists in the feed
              const existsInFeed = currentFeedItems.some(
                (item) => item.filename === newAudio.filename
              );

              // Only add to feedItems if it doesn't exist
              if (!existsInFeed) {
                // Add the new item to the existing feed items
                setFeedItems((prevItems) => [newAudio, ...prevItems]);

                // Set this as the new item for badge/styling
                setNewItemId(newAudio.filename);

                // Set this as the item to animate (separate from just being "new")
                setAnimateItemId(newAudio.filename);
              }
            }

            if (message.type === 'payment_success') {
              // eslint-disable-next-line no-console
              console.log(`[WebSocket ${wsConnectionId}] Received payment success:`, message.data);

              // Format the message based on who placed the sticker
              const isCurrentUserSticker =
                !!pendingStripeSession && message.data.sessionId === pendingStripeSession;

              if (isCurrentUserSticker) {
                if (message.data.stickerCount === 1)
                  setSuccessMessage('Your sticker has been placed successfully!');
                else if (message.data.stickerCount > 1)
                  setSuccessMessage(`Your stickers have been placed successfully!`);

                // Cancel any pending polling timeout
                if (pollingTimeoutId) {
                  clearTimeout(pollingTimeoutId);
                  setPollingTimeoutId(null);
                }
              } else {
                if (message.data.stickerCount === 1)
                  setSuccessMessage('Someone just placed a new sticker on the wall!');
                else if (message.data.stickerCount > 1)
                  setSuccessMessage(`Someone just placed new stickers on the wall!`);
              }

              // Show success notification
              setShowPaymentSuccess(true);

              // If this is our sticker, clean up the pending session
              if (isCurrentUserSticker) {
                setPendingStripeSession(null);
                setIsProcessingPayment(false);
              }

              // Notify components to reload stickers
              saveSticker();

              // Clear pending stickers if this is our payment
              if (isCurrentUserSticker) {
                setPendingStickers([]);
              }
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              `[WebSocket ${wsConnectionId}] Error handling message:`,
              error,
              event.data
            );
          }
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`[WebSocket ${wsConnectionId}] Error setting up WebSocket:`, error);
      }
    };

    // Connect on component mount or when connection attempts change
    connectWebSocket();

    // Clean up on unmount
    return () => {
      // eslint-disable-next-line no-console
      console.log(`[WebSocket ${wsConnectionId}] Cleanup - component unmounting`);
      isActive = false;
      if (pingTimeoutId) clearTimeout(pingTimeoutId);
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }

      // Also clear any pending newItem timeout
      if (newItemTimeoutRef.current) {
        clearTimeout(newItemTimeoutRef.current);
        newItemTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionAttempts]);

  const handleAboutOpen = () => {
    setAboutOpen(true);
  };

  const handleAboutClose = () => {
    setAboutOpen(false);
  };

  // Toggle sticker mode
  const toggleStickerMode = () => {
    if (stickerMode) {
      // If already in sticker mode, exit it and clear all pending stickers
      handleCancelStickerMode();
    } else {
      // If not in sticker mode, open file picker and turn on lights
      fileInputRef.current.click();
      // Turn on the lights when entering sticker mode
      setLightMode(true);
    }
  };

  // Toggle light mode
  const toggleLightMode = () => {
    setLightMode((prev) => !prev);
  };

  // Handle sticker file upload
  const handleStickerFileChange = (event) => {
    const file = event.target.files[0];

    // Check if file is SVG and larger than 30KB
    if (file && file.type === 'image/svg+xml' && file.size > 30 * 1024) {
      setStickerUploadError('SVG files larger than 30KB are not supported');
      event.target.value = '';
      return;
    }

    if (file && file.type.startsWith('image/')) {
      setUserUploadedStickerImage(file);

      setStickerMode(true);
      setLightMode(true);
    }

    // Clear the input value so the same file can be selected again
    event.target.value = '';
  };

  // Handle sticker placing state change
  const handlePlacingChange = (isPlacing) => {
    setIsPlacingSticker(isPlacing);
  };

  // Handle canceling sticker mode completely
  const handleCancelStickerMode = () => {
    setStickerMode(false);
    setStickerPosition(null);
    // Clear all pending stickers if exiting sticker mode completely
    setPendingStickers([]);
  };

  // Handle sticker placement confirmation
  const handleConfirmSticker = () => {
    if (!stickerPosition) return;

    // Create a sticker entry with position and rotation information
    const newSticker = {
      position: {
        x: stickerPosition.point.x,
        y: stickerPosition.point.y,
        z: stickerPosition.point.z,
      },
      normal: {
        x: stickerPosition.normal.x,
        y: stickerPosition.normal.y,
        z: stickerPosition.normal.z,
      },
      rotationAngle: stickerPosition.rotationAngle || 0,
      scale: stickerPosition.scale || 1,
      timestamp: Date.now(),
    };

    // If a custom image was uploaded, read it now
    if (userUploadedStickerImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        // Add the sticker to the pending stickers array with image data
        newSticker.imageData = e.target.result;
        setPendingStickers((prevStickers) => [...prevStickers, newSticker]);

        // Reset sticker position to allow placing another sticker
        setStickerPosition(null);
        setIsPlacingSticker(false);

        // Reset the sticker image but keep sticker mode active
        // The user needs to explicitly click "add another sticker" to place a new one
        setUserUploadedStickerImage(null);
      };
      reader.readAsDataURL(userUploadedStickerImage);
    } else {
      // eslint-disable-next-line no-console
      console.warn('User sticker upload failed');
    }
  };

  // Process Stripe payment for all pending stickers
  const processStripePayment = async () => {
    try {
      setIsProcessingPayment(false);

      if (pendingStickers.length === 0) {
        setPaymentError('No stickers to purchase.');
        setIsProcessingPayment(false);
        return;
      }

      // Call the server-side endpoint to create a Stripe checkout session
      const response = await axios.post(`${API_URL}/buy-sticker`, {
        stickers: pendingStickers,
        returnUrl: window.location.origin,
      });

      // Get the client secret and session ID
      const { clientSecret, sessionId } = response.data;

      // Store the session ID and count in state
      setPendingStripeSession(sessionId);

      // Show the embedded checkout
      setCheckoutClientSecret(clientSecret);
      setShowEmbeddedCheckout(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating payment session:', err);
      setPaymentError(
        err.response?.data?.message || 'Failed to create payment session. Please try again.'
      );
      setIsProcessingPayment(false);
    }
  };

  // Save stickers to server
  const saveSticker = () => {
    // The stickers are now saved server-side when payment is successful
    // We just need to notify components to reload stickers
    setStickerPosition(null);
    setStickerMode(false);
    setUserUploadedStickerImage(null);

    // Notify other components that stickers have been updated
    document.dispatchEvent(new Event('stickersUpdated'));
  };

  // Handle sticker placement cancellation
  const handleCancelSticker = () => {
    // Reset the current placement state
    setStickerPosition(null);
  };

  // Add another sticker
  const handleAddAnotherSticker = () => {
    // Reset sticker position and open file picker
    setStickerPosition(null);
    fileInputRef.current.click();
  };

  // Handle canceling a payment
  const handleCancelPayment = () => {
    // Clear the pending session
    setPendingStripeSession(null);

    // Cancel any pending polling timeout
    if (pollingTimeoutId) {
      clearTimeout(pollingTimeoutId);
      setPollingTimeoutId(null);
    }

    setIsProcessingPayment(false);
  };

  // Handle closing the payment error notification
  const handleClosePaymentError = () => {
    setPaymentError(null);
  };

  // Add a handler for closing the success notification
  const handleClosePaymentSuccess = () => {
    setShowPaymentSuccess(false);
  };

  // Add a handler for closing the sticker upload error notification
  const handleCloseStickerUploadError = () => {
    setStickerUploadError(null);
  };

  // Add cleanup for the polling timeout in a useEffect
  useEffect(() => {
    return () => {
      if (pollingTimeoutId) {
        clearTimeout(pollingTimeoutId);
      }
    };
  }, [pollingTimeoutId]);

  // Save pending stickers to sessionStorage whenever they change
  useEffect(() => {
    if (pendingStickers.length > 0) {
      sessionStorage.setItem('pendingStickers', JSON.stringify(pendingStickers));
    } else {
      sessionStorage.removeItem('pendingStickers');
    }
  }, [pendingStickers]);

  // Clear pending stickers on page refresh/unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('pendingStickers');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle payment success
  const handlePaymentSuccess = () => {
    // Clear the pending session
    setPendingStripeSession(null);

    // Update UI state
    setIsProcessingPayment(false);
    setShowEmbeddedCheckout(false);
    setCheckoutClientSecret(null);

    // Clear pending stickers after successful payment
    setPendingStickers([]);

    // Show success message
    setShowPaymentSuccess(true);

    // Notify components to reload stickers
    saveSticker();
  };

  const handleCloseCheckout = () => {
    setShowEmbeddedCheckout(false);
    setCheckoutClientSecret(null);
    setIsProcessingPayment(false);
  };

  // Render content based on current route
  const renderContent = () => {
    if (location.pathname === '/') {
      return (
        <HomePage
          onNavigate={navigate}
          onProcessed={handleProcessed}
          processedFilename={processedFilename}
          onReset={handleReset}
        />
      );
    } else if (location.pathname === '/feed') {
      return (
        <FeedPage
          onNavigate={navigate}
          feedItems={feedItems}
          loading={loading}
          error={error}
          newItemId={newItemId}
          animateItemId={animateItemId}
          loadMoreItems={loadMoreItems}
          hasMore={hasMore}
        />
      );
    } else {
      // 404 page
      return <Typography variant="h4">you're lost (404)</Typography>;
    }
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100svw',
          height: '100svh',
        }}
      >
        <Canvas shadows camera={{ position: [0, 2, 5], fov: 45 }}>
          <Suspense fallback={null}>
            <AnimatedCamera />
            <BathroomScene
              stickerMode={stickerMode}
              setStickerPosition={setStickerPosition}
              stickerPosition={stickerPosition}
              userUploadedStickerImage={userUploadedStickerImage}
              onPlacingChange={handlePlacingChange}
              lightMode={lightMode}
              pendingStickers={pendingStickers}
            />
          </Suspense>
        </Canvas>
      </div>
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          width: '100%',
          px: 4,
          py: 2,
          mb: 'auto',
          textAlign: 'right',
          zIndex: 1000,
          filter: lightMode ? 'brightness(.4)' : 'brightness(1.)',
          transition: 'filter 1s ease',
        }}
      >
        <Box
          maxWidth="md"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mx: 'auto',
          }}
        >
          {/* Left side - Sticker placement controls */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {stickerMode && isPlacingSticker && (
              <>
                <Typography variant="body2" color="text.secondary">
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleCancelSticker}
                    color="error"
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textUnderlineOffset: '4px',
                      textDecorationColor: 'rgba(244, 67, 54, 0.5)',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    undo
                  </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleConfirmSticker}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textUnderlineOffset: '4px',
                      textDecorationColor: 'rgba(187, 134, 252, 0.5)',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    place sticker
                  </Link>
                </Typography>
              </>
            )}

            {/* Checkout button when stickers are placed but not currently placing */}
            {stickerMode && !isPlacingSticker && pendingStickers.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setIsProcessingPayment(true)}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textUnderlineOffset: '4px',
                      textDecorationColor: 'rgba(187, 134, 252, 0.5)',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {pendingStickers.length > 1
                      ? `place stickers forever ($${pendingStickers.length})`
                      : 'place sticker forever ($1)'}
                  </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleAddAnotherSticker}
                    color="secondary"
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textUnderlineOffset: '4px',
                      textDecorationColor: 'rgba(138, 152, 157, 0.5)',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    add another sticker
                  </Link>
                </Typography>
              </>
            )}
          </Box>

          {/* Right side - Main controls */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <Link
                component="button"
                variant="body2"
                onClick={toggleStickerMode}
                color="secondary"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '4px',
                  textDecorationColor: 'rgba(138, 152, 157, 0.5)',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {stickerMode ? 'stop sticker bombing' : 'add stickers'}
              </Link>
            </Typography>

            <Typography variant="body2" color="text.secondary">
              <Link
                component="button"
                variant="body2"
                onClick={toggleLightMode}
                color="secondary"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '4px',
                  textDecorationColor: 'rgba(138, 152, 157, 0.5)',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {lightMode ? 'lights off' : 'lights on'}
              </Link>
            </Typography>

            <Typography variant="body2" color="text.secondary">
              <Link
                component="button"
                variant="body2"
                onClick={handleAboutOpen}
                color="secondary"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '4px',
                  textDecorationColor: 'rgba(138, 152, 157, 0.5)',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                about
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>

      <Container
        maxWidth="md"
        className="app-container"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          pointerEvents: lightMode ? 'none' : 'auto',
        }}
      >
        {/* Hidden file input for sticker image selection */}
        <input
          accept="image/*"
          id="sticker-file-input"
          type="file"
          onChange={handleStickerFileChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />

        <Box
          className="app-box"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            marginTop: '2rem',
            marginBottom: '2rem',
            opacity: stickerMode || lightMode ? 0.15 : 1,
            transition: 'opacity 1.5s ease',
            pointerEvents: stickerMode || lightMode ? 'none' : 'auto',
          }}
        >
          {renderContent()}
        </Box>

        {/* About Dialog */}
        <Dialog open={aboutOpen} onClose={handleAboutClose} aria-labelledby="about-dialog-title">
          <DialogTitle id="about-dialog-title">Okay, so...</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              We don't store your original audio
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Also you're anonymous around here so you can let it all out :)
            </Typography>
            <Typography sx={{ mt: 4 }} variant="body1">
              Attribution:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Person crying is{' '}
              <Link target="_blank" href="https://freesound.org/people/tweedledee3/">
                tweedledee3
              </Link>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Crowd ambience (quiet) was recorded by{' '}
              <Link target="_blank" href="https://freesound.org/people/Pfannkuchn/">
                Pfannkuchn
              </Link>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Bathroom stall created by{' '}
              <Link target="_blank" href="https://www.instagram.com/bradley_corporation/">
                Bradley Corporation
              </Link>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Website by{' '}
              <Link target="_blank" href="https://lukasoppenheimer.net">
                Lukas Oppenheimer
              </Link>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAboutClose} color="primary">
              close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      {/* Payment Error Notification */}
      <Snackbar
        open={!!paymentError}
        autoHideDuration={6000}
        onClose={handleClosePaymentError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClosePaymentError} severity="error" sx={{ width: '100%' }}>
          {paymentError}
        </Alert>
      </Snackbar>

      {/* Payment Processing Indicator */}
      <Dialog open={isProcessingPayment}>
        <DialogTitle>
          Ready to place your {pendingStickers.length > 1 ? 'stickers' : 'sticker'}?
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>This is how you can throw me a bone :)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {pendingStickers.length > 1
              ? `Your ${pendingStickers.length} stickers will be permanently placed on the bathroom wall for $${pendingStickers.length}.`
              : 'Your sticker will be permanently placed on the bathroom wall for $1.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button onClick={handleCancelPayment} color="error" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={processStripePayment}
            color="primary"
            variant="contained"
            startIcon={<CheckIcon />}
          >
            Pay Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Success Notification */}
      <Snackbar
        open={!!showPaymentSuccess}
        autoHideDuration={6000}
        onClose={handleClosePaymentSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleClosePaymentSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Sticker Upload Error Notification */}
      <Snackbar
        open={!!stickerUploadError}
        autoHideDuration={6000}
        onClose={handleCloseStickerUploadError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseStickerUploadError} severity="error" sx={{ width: '100%' }}>
          {stickerUploadError}
        </Alert>
      </Snackbar>

      {/* Embedded Stripe Checkout */}
      {showEmbeddedCheckout && checkoutClientSecret && (
        <StripeCheckout
          clientSecret={checkoutClientSecret}
          onClose={handleCloseCheckout}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/*" element={<MainContent />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
